import {
  clearRefreshToken,
  getRefreshToken,
  storeRefreshToken,
} from './tokenStore';

export const ACCESS_TOKEN_KEY = '__understory_at';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Mutex for refresh requests. When multiple API calls get 401 simultaneously,
 * only ONE refresh fires — all others await the same promise. Without this,
 * concurrent 401s cause multiple rotations; the second rotation presents an
 * already-revoked token and triggers theft detection.
 */
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const refreshToken = await getRefreshToken();
      if (!refreshToken) return null;

      const res = await fetch('/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) {
        await clearRefreshToken();
        return null;
      }

      const data = (await res.json()) as {
        access_token?: string;
        refresh_token?: string;
      };

      // Store the ROTATED refresh token — old one is now revoked server-side.
      if (data.refresh_token) {
        await storeRefreshToken(data.refresh_token);
      }

      if (data.access_token) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
        return data.access_token;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Brief delay so near-simultaneous callers coalesce, then the mutex
      // releases for a fresh attempt on the next wave.
      setTimeout(() => {
        refreshPromise = null;
      }, 200);
    }
  })();

  return refreshPromise;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Fetch wrapper that auto-attaches the bearer token and retries once on 401
 * after a silent refresh. If refresh fails, redirects to /auth/login.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const response = await rawApiFetch(path, options);

  if (!response.ok) {
    let code = 'http_error';
    let message = response.statusText;
    try {
      const payload = (await response.json()) as {
        error?: string;
        message?: string;
      };
      code = payload.error ?? code;
      message = payload.message ?? message;
    } catch {
      // Response wasn't JSON — keep defaults.
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Lower-level variant that returns the raw Response. Use when you need status
 * codes, streaming, or non-JSON bodies. Auto-refresh still applies.
 */
export async function rawApiFetch(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const doFetch = async (accessToken: string | null): Promise<Response> => {
    const headers = new Headers(options.headers);
    if (options.body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

    return fetch(path, {
      ...options,
      headers,
      body:
        options.body !== undefined
          ? typeof options.body === 'string'
            ? options.body
            : JSON.stringify(options.body)
          : undefined,
    });
  };

  let token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  let response = await doFetch(token);

  if (response.status === 401) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      response = await doFetch(fresh);
    } else {
      const returnTo = encodeURIComponent(
        window.location.pathname + window.location.search + window.location.hash,
      );
      window.location.href = `/auth/login?return_to=${returnTo}`;
      throw new ApiError(401, 'session_expired', 'Session expired');
    }
  }

  return response;
}
